/**
 * In-memory repository for the site-configuration CMS, backed by Postgres.
 *
 * Pure and synchronous: every mutation takes a store and returns a new one.
 * This module holds no connection of its own -- the hook pulls from Postgres
 * and seeds the cache here, and the sync bridge pushes writes back.
 *
 * Image FILE BYTES do not pass through here: they go to a Supabase storage
 * bucket, and only the resulting public URL is stored, as an ordinary string
 * field. See shared/components/ImageField.tsx.
 *
 * Every mutation returns a NEW store object so React state updates propagate.
 */

import { ulid } from "ulid"
import { isSiteLocale, unsupportedLocaleMessage } from "../shared/siteLanguages"
import {
  type SiteConfigStore, type SiteConfigEntityKind, type Site, type MenuItem,
  type PageContent, type TranslatedPageContent, type PageContentType,
  SITECONFIG_BUCKET, emptySiteConfigStore, siteConfigDisplayName,
} from "./types"

export function emptyMenuItem(type: MenuItem["type"] = "link"): MenuItem {
  return { id: ulid(), type, title: {}, children: [] }
}

const KEY_PREFIX = "svc-siteconfig:"
const DEFAULT_DB = "default"

function keyFor(id: string) { return `${KEY_PREFIX}${id}` }
function now() { return Date.now() }
function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)) }

/**
 * A fresh, empty store.
 *
 * There is no local cache: the hook pulls the real store from Postgres on
 * mount and on every team switch, so anything held here would be a third
 * copy of state that React and the store ref already own -- and one that
 * silently went stale on a reload.
 */
export function emptyStoreFor(): SiteConfigStore {
  return emptySiteConfigStore()
}

// ── Generic access ───────────────────────────────────────────────────────────

export function getEntity(store: SiteConfigStore, kind: SiteConfigEntityKind, id: string): any | null {
  const bucket = store[SITECONFIG_BUCKET[kind]] as Record<string, any>
  return bucket?.[id] ?? null
}

export function listEntities(store: SiteConfigStore, kind: SiteConfigEntityKind): any[] {
  const bucket = store[SITECONFIG_BUCKET[kind]] as Record<string, any>
  return Object.values(bucket ?? {})
}

export function countEntities(store: SiteConfigStore, kind: SiteConfigEntityKind): number {
  return Object.keys((store[SITECONFIG_BUCKET[kind]] as Record<string, any>) ?? {}).length
}

export function displayName(store: SiteConfigStore, kind: SiteConfigEntityKind, id: string, locale: string): string {
  const e = getEntity(store, kind, id)
  return siteConfigDisplayName(kind, e, locale, store.defaultLocale)
}

/**
 * Creates an entity in the given bucket.
 *
 * `data` supplies the entity's own fields; id/timestamps/array-defaults are
 * managed here so every entity is consistent.
 */
export function createEntity(
  store: SiteConfigStore,
  kind: SiteConfigEntityKind,
  data: Record<string, any>
): { store: SiteConfigStore; id: string } {
  const next = clone(store)
  const id = ulid()

  const base: any = { id, createdAt: now(), updatedAt: now() }

  if (kind === "site") {
    // Geography is referenced, not owned: several sites may scope the same
    // country, so there is no claim check here (see FIDELITY NOTE 1).
    Object.assign(base, {
      locales: [], googleTag: [], contentIds: [], headerMenu: [],
      footer: { footerLinks: [] },
      serviceMap: { countryIds: [], regionIds: [], cityIds: [] },
      articles: { allCategories: true, categoryIds: [] },
    })
  }
  if (kind === "pageContent") {
    if (!data.type) throw new Error("A pageContent block requires a type.")
    Object.assign(base, { translations: {} })
  }

  const bucket = next[SITECONFIG_BUCKET[kind]] as Record<string, any>
  bucket[id] = { ...base, ...data }
  return { store: next, id }
}

// Rendered directly by the public site (logo goes into <img src>), so a locale map breaks them
const PLAIN_STRING_SITE_FIELDS = new Set(["logo"])

// Catches whole-page pastes into single-line fields (an aboutTitle once held ~414 chars of CMS UI text)
const MAX_SHORT_TEXT = 300

const SHORT_TEXT_FIELDS = new Set([
  "title", "name", "aboutTitle", "missionTitle", "servicesTitle", "heading",
])

// This app's own UI text; kept narrow so real content is never rejected
const UI_CHROME_MARKERS = [
  /Smart CMS\s+Local\b/i,
  /\b\d+\s+(?:site|block|header nav item)s?\s*·/i,
]

// Why `value` can't be stored in `field`, or null when it can
export function fieldWriteBlocker(field: string, value: unknown): string | null {
  if (value == null) return null

  if (PLAIN_STRING_SITE_FIELDS.has(field) && typeof value === "object") {
    return `${field} must be a plain URL string, not a per-locale map — ` +
      `the public site renders it straight into an <img src>, where an object becomes "[object Object]".`
  }

  if (typeof value !== "string") return null

  if (SHORT_TEXT_FIELDS.has(field) && value.length > MAX_SHORT_TEXT) {
    return `${field} is ${value.length} characters; the limit is ${MAX_SHORT_TEXT}. ` +
      `This is usually a whole page of text pasted into a single-line field — check what was copied.`
  }

  if (UI_CHROME_MARKERS.some(re => re.test(value))) {
    return `${field} contains Smart CMS interface text, not site content. ` +
      `This happens when a full page is selected and pasted, or when a tool captures rendered page text instead of a field value.`
  }

  return null
}

export function updateEntity(
  store: SiteConfigStore,
  kind: SiteConfigEntityKind,
  id: string,
  patch: Record<string, any>
): SiteConfigStore {
  const next = clone(store)
  const bucket = next[SITECONFIG_BUCKET[kind]] as Record<string, any>
  const entity = bucket[id]
  if (!entity) throw new Error(`No such ${kind}: ${id}`)
  // Never let a patch clobber identity or creation time.
  const { id: _i, createdAt: _c, translations: _t, ...safe } = patch

  // Validated here because every site-field write (editor and agent) goes through this
  for (const [field, value] of Object.entries(safe)) {
    const blocker = fieldWriteBlocker(field, value)
    if (blocker) throw new Error(blocker)
  }
  // A site can only start publishing languages the sites can show; codes it already lists can stay or go
  if (kind === "site") {
    const had = new Set<string>([...(entity.locales ?? []), entity.defaultLocale].filter(Boolean))
    const added = [...(Array.isArray(safe.locales) ? safe.locales : []), ...(typeof safe.defaultLocale === "string" ? [safe.defaultLocale] : [])]
      .filter((c: string) => c && !had.has(c) && !isSiteLocale(c))
    if (added.length) throw new Error(unsupportedLocaleMessage(added[0]))
  }

  Object.assign(entity, safe, { updatedAt: now() })
  return next
}

/**
 * Upserts one locale's translation on a translatable entity. Only pageContent
 * uses this — site has no per-locale translations of its own (the
 * cookie banner is a separate map, and headerMenu/footer carry their locale
 * maps inline rather than through this mechanism).
 */
export function setTranslation(
  store: SiteConfigStore,
  kind: SiteConfigEntityKind,
  id: string,
  locale: string,
  patch: Record<string, any>
): SiteConfigStore {
  if (kind === "site") throw new Error("site has no per-locale translations (see setTranslation's doc comment).")
  const next = clone(store)
  const bucket = next[SITECONFIG_BUCKET[kind]] as Record<string, any>
  const entity = bucket[id]
  if (!entity) throw new Error(`No such ${kind}: ${id}`)
  // Translated fields need the same check as updateEntity
  for (const [field, value] of Object.entries(patch)) {
    const blocker = fieldWriteBlocker(field, value)
    if (blocker) throw new Error(blocker)
  }

  entity.translations ||= {}
  if (!entity.translations[locale] && !isSiteLocale(locale)) throw new Error(unsupportedLocaleMessage(locale))
  const existing = entity.translations[locale] ?? {}
  entity.translations[locale] = { ...existing, ...patch, locale, updatedAt: now() }
  entity.updatedAt = now()
  return next
}

export interface TranslationWrite {
  kind: SiteConfigEntityKind
  id: string
  locale: string
  translation: Record<string, any> // replaces the locale outright, unlike setTranslation's merge
}

// Many translation writes with one store clone. onBefore gets each entity's pre-write copy for history.
// Missing entities and the `site` kind are skipped and reported, not thrown
export function setTranslationsBulk(
  store: SiteConfigStore,
  writes: TranslationWrite[],
  onBefore?: (write: TranslationWrite, before: Record<string, any>) => void,
): { store: SiteConfigStore; applied: number; missing: TranslationWrite[] } {
  const next = clone(store)
  const missing: TranslationWrite[] = []
  let applied = 0

  for (const w of writes) {
    if (w.kind === "site") { missing.push(w); continue }
    const bucket = next[SITECONFIG_BUCKET[w.kind]] as Record<string, any>
    const entity = bucket?.[w.id]
    if (!entity) { missing.push(w); continue }

    if (!entity.translations?.[w.locale] && !isSiteLocale(w.locale)) throw new Error(unsupportedLocaleMessage(w.locale))
    onBefore?.(w, clone(entity))

    entity.translations ||= {}
    entity.translations[w.locale] = { ...w.translation, locale: w.locale, updatedAt: now() }
    entity.updatedAt = now()
    applied++
  }

  return { store: next, applied, missing }
}

// ── Referential integrity ────────────────────────────────────────────────────

/**
 * Everything that points at `id`, so a delete can report (or refuse) rather
 * than silently orphaning rows.
 */
export function findReferences(
  store: SiteConfigStore,
  kind: SiteConfigEntityKind,
  id: string
): Array<{ kind: SiteConfigEntityKind; id: string; field: string }> {
  const refs: Array<{ kind: SiteConfigEntityKind; id: string; field: string }> = []
  const add = (k: SiteConfigEntityKind, entityId: string, field: string) => refs.push({ kind: k, id: entityId, field })

  if (kind === "pageContent") {
    for (const site of Object.values(store.sites)) if (site.contentIds.includes(id)) add("site", site.id, "contentIds")
  }

  return refs
}

/**
 * Deletes an entity and detaches every reference to it (nulls scalars, prunes
 * id arrays), matching the SET NULL behaviour Directus applies to these FKs.
 */
export function deleteEntity(
  store: SiteConfigStore,
  kind: SiteConfigEntityKind,
  id: string
): { store: SiteConfigStore; detached: number } {
  const next = clone(store)
  const bucket = next[SITECONFIG_BUCKET[kind]] as Record<string, any>
  if (!bucket[id]) throw new Error(`No such ${kind}: ${id}`)

  const refs = findReferences(next, kind, id)
  for (const ref of refs) {
    const target = (next[SITECONFIG_BUCKET[ref.kind]] as Record<string, any>)[ref.id]
    if (!target) continue
    if (Array.isArray(target[ref.field])) {
      target[ref.field] = target[ref.field].filter((x: string) => x !== id)
    } else {
      target[ref.field] = undefined
    }
    target.updatedAt = now()
  }

  delete bucket[id]
  return { store: next, detached: refs.length }
}

// ── Queries ──────────────────────────────────────────────────────────────────

/** Every PageContent block attached to a Site, in the order stored. */
export function pageContentsOfSite(store: SiteConfigStore, siteId: string): PageContent[] {
  const site = store.sites[siteId]
  if (!site) return []
  return site.contentIds.map(id => store.pageContents[id]).filter(Boolean)
}


/** Flattens a Site's headerMenu tree into a single list, depth-first. */
export function flattenMenu(items: MenuItem[]): MenuItem[] {
  const out: MenuItem[] = []
  for (const item of items) {
    out.push(item)
    if (item.children?.length) out.push(...flattenMenu(item.children))
  }
  return out
}

// Whether a site has any text in `locale`: header titles, footer text and links, or the cookie banner
export function siteHasLocale(site: any, locale: string): boolean {
  const has = (v: unknown) => typeof v === "string" && v.trim() !== ""
  return flattenMenu(site?.headerMenu ?? []).some(m => has(m.title?.[locale]))
    || has(site?.footer?.text?.[locale])
    || (site?.footer?.footerLinks ?? []).some((f: any) => has(f.title?.[locale]))
    || has(site?.cookieBannerTranslations?.[locale])
}

/** Deep-clones a headerMenu tree so a mutation doesn't touch the caller's store. */
export function cloneMenu(items: MenuItem[]): MenuItem[] {
  return JSON.parse(JSON.stringify(items))
}

/** Finds one item anywhere in a headerMenu tree by id. */
export function findMenuItem(items: MenuItem[], id: string): MenuItem | null {
  for (const item of items) {
    if (item.id === id) return item
    if (item.children?.length) {
      const found = findMenuItem(item.children, id)
      if (found) return found
    }
  }
  return null
}

/** Removes one item (and its subtree) from a headerMenu tree by id. Returns a new tree. */
export function removeMenuItem(items: MenuItem[], id: string): MenuItem[] {
  return items
    .filter(item => item.id !== id)
    .map(item => item.children?.length ? { ...item, children: removeMenuItem(item.children, id) } : item)
}

export function stats(store: SiteConfigStore) {
  const byType: Record<string, number> = {}
  for (const pc of Object.values(store.pageContents)) byType[pc.type] = (byType[pc.type] ?? 0) + 1
  let headerMenuItems = 0
  let footerLinks = 0
  for (const site of Object.values(store.sites)) {
    headerMenuItems += flattenMenu(site.headerMenu ?? []).length
    footerLinks += site.footer?.footerLinks?.length ?? 0
  }
  return {
    sites: countEntities(store, "site"),
    headerMenuItems,
    footerLinks,
    pageContents: countEntities(store, "pageContent"),
    pageContentsByType: byType,
  }
}

/**
 * Ranked keyword search across site configs (including their embedded
 * headerMenu/footer text) and page content.
 */
export function searchStore(
  store: SiteConfigStore,
  query: string,
  opts: { limit?: number; kinds?: SiteConfigEntityKind[] } = {}
): Array<{ kind: SiteConfigEntityKind; id: string; name: string; score: number }> {
  const q = query.trim().toLowerCase()
  if (!q || q.length < 2) return []
  const kinds = opts.kinds ?? (["site", "pageContent"] as SiteConfigEntityKind[])
  const out: Array<{ kind: SiteConfigEntityKind; id: string; name: string; score: number }> = []

  const scoreText = (text: string): number => {
    const t = text.toLowerCase()
    if (t === q) return 100
    if (t.startsWith(q)) return 80
    if (t.includes(` ${q}`) || t.includes(`${q} `)) return 60
    if (t.includes(q)) return 40
    return 0
  }

  for (const kind of kinds) {
    for (const entity of listEntities(store, kind)) {
      const name = siteConfigDisplayName(kind, entity, store.defaultLocale, store.defaultLocale)
      let best = scoreText(name)
      // Also search every translation's title/text fields, and (for
      // pageContent) the base-row content fields, not just the display name.
      const haystacks: string[] = []
      if (entity.translations) {
        for (const t of Object.values(entity.translations) as any[]) {
          for (const v of Object.values(t)) if (typeof v === "string") haystacks.push(v)
        }
      }
      if (kind === "pageContent") {
        for (const v of Object.values(entity)) if (typeof v === "string") haystacks.push(v)
      }
      if (kind === "site") {
        for (const item of flattenMenu(entity.headerMenu ?? [])) {
          haystacks.push(...(Object.values(item.title ?? {}) as string[]))
        }
        for (const link of entity.footer?.footerLinks ?? []) {
          haystacks.push(...(Object.values(link.title ?? {}) as string[]))
        }
        if (entity.footer?.text) haystacks.push(...(Object.values(entity.footer.text) as string[]))
      }
      for (const h of haystacks) best = Math.max(best, scoreText(h) * 0.8)
      if (best > 0) out.push({ kind, id: entity.id, name, score: best })
    }
  }

  return out.sort((a, b) => b.score - a.score).slice(0, opts.limit ?? 25)
}
